import { useEffect, useState, useRef } from 'react';
import { Layout } from '@/components/common/Layout/Layout';
import { PageBody, PageHeader, PageShell } from '@/components/ui/page-shell';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import api from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ComboboxSelect, useComboboxOptions } from '@/components/ui/combobox-select';
import { DatePicker } from '@/components/ui/date-picker';
import { Switch } from '@/components/ui/switch';
import {
  List,
  ArrowLeft,
  ArrowRight,
  Download,
  RotateCcw,
  Trash
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { decimalToHHMM, hhmmToDecimal } from '@/utils/time-format';
import { toISODate } from '@/lib/dates';
import { getRecentMatterIds, pushRecentMatter } from '@/lib/recent-matters';
import { isSuperUser as checkSuperUser, isAdmin as checkAdmin, isManager as checkManager, canViewAllUsers as checkCanViewAllUsers } from '@/lib/permissions';


interface DayEntry {
  hours: number;
  entryId?: number;
}

interface WeeklyMatrixEntry {
  // Use a unique key for React list rendering if possible, otherwise rely on index
  tempKey?: string; // Temporary key for React rendering during updates
  matter: number;
  description: string;
  billable: boolean;
  days: {
    [day: string]: DayEntry; // dayKey format 'yyyy-MM-dd'
  };
  matterName?: string;
  reviewed?: boolean; // Keep for display purposes if needed
  userId?: number; // Track which user this entry belongs to
  _sortGroup?: string; // Internal field for sorting
  _sortName?: string; // Internal field for sorting
}

export function TimesheetWeek() {
  const { user } = useAuth();
  const userGroups = user?.groups || [];
  const { matters, users, clients, fetchAll, isLoading: storeLoading } = useStore();

  // Add useRef for textarea auto-resize and debounced saving
  const textareaRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({});
  const saveTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Auto-resize function that can be called programmatically
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(40, textarea.scrollHeight)}px`;
    }
  };

  // Debounced save function for time entries
  const debouncedSaveTimeEntry = (index: number, dayKey: string, hours: number, delay: number = 1000) => {
    const saveKey = `${index}-${dayKey}`;
    
    // Clear existing timer for this specific entry
    if (saveTimers.current[saveKey]) {
      clearTimeout(saveTimers.current[saveKey]);
    }
    
    // Set new timer
    saveTimers.current[saveKey] = setTimeout(() => {
      // Check if we still have valid entries and the entry still exists
      if (weeklyEntries[index]) {
        const entry = weeklyEntries[index];
        // Only save if we have matter and description
        if (entry.matter && entry.description) {
          createOrUpdateDayEntry(index, dayKey, hours);
        }
      }
      delete saveTimers.current[saveKey];
    }, delay);
  };

  // Create combobox options for matters (only active matters, sorted alphabetically)
  const baseMatterOptions = useComboboxOptions(
    matters
      .filter(m => m.status === 'active')
      .sort((a, b) => {
        // Sort by group name first, then by matter name
        const groupA = (a.group_name || '').toLowerCase();
        const groupB = (b.group_name || '').toLowerCase();
        if (groupA !== groupB) {
          return groupA.localeCompare(groupB);
        }
        return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      }),
    (m) => m.id.toString(),
    (m) => m.group_name ? `${m.group_name} - ${m.name}` : m.name,
    (m) => {
      // Include client name in searchable text
      const activeConfig = m.fee_sharing_configs?.find(c => c.is_active) || m.fee_sharing_configs?.[0];
      const clientNames = activeConfig?.participants?.map(p => {
        const client = clients.find(c => c.id === p.client);
        return client?.name || '';
      }).join(' ') || '';
      return `${m.name} ${m.group_name || ''} ${clientNames}`;
    }
  );

  // Recently-used matters first (in recency order), rest unchanged
  const recentMatterIds = getRecentMatterIds();
  const recentMatterOptions = recentMatterIds
    .map((id) => baseMatterOptions.find((o) => o.value === id))
    .filter((o): o is NonNullable<typeof o> => Boolean(o));
  const matterOptions = recentMatterOptions.length > 0
    ? [
        ...recentMatterOptions,
        ...baseMatterOptions.filter((o) => !recentMatterIds.includes(o.value)),
      ]
    : baseMatterOptions;

  // State variables
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [weeklyEntries, setWeeklyEntries] = useState<WeeklyMatrixEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(user?.id || null);
  // Store input values temporarily while editing
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({}); // key: `${index}-${dayKey}`

  // User role and permission flags
  const isSuperUser = checkSuperUser(userGroups);
  const isAdmin = checkAdmin(userGroups);
  const isManager = checkManager(userGroups);
  const canViewAllUsers = checkCanViewAllUsers(userGroups);

  // Generate days of the current week
  const daysOfWeek = eachDayOfInterval({
    start: currentWeekStart,
    end: endOfWeek(currentWeekStart, { weekStartsOn: 1 })
  });

  // Initialize data
  useEffect(() => {
    fetchAll();
  }, []);

  // Format matter name
  const formatMatterName = (matterId: number) => {
    const matter = matters.find(m => m.id === matterId);
    return matter ? (matter.group_name ? `${matter.group_name} - ${matter.name}` : matter.name) : "Unknown Matter";
  };

  // Navigation functions
  const previousWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  const nextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const currentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const fetchTimeEntries = async (skipUpdateIfSame: boolean = false) => {
    if (!skipUpdateIfSame) {
      setIsLoading(true);
      setInputValues({}); // Clear temporary input values on fetch
      
      // Clear all pending save timers when fetching new data
      Object.values(saveTimers.current).forEach(timer => clearTimeout(timer));
      saveTimers.current = {};
    }
    
    try {
      const queryParams = new URLSearchParams();
      if (selectedUserId !== null) {
        queryParams.append('user_id', selectedUserId.toString());
      }
      const weekStart = format(currentWeekStart, 'yyyy-MM-dd');
      const weekEnd = format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      queryParams.append('start_date', weekStart);
      queryParams.append('end_date', weekEnd);

      const response = await api.get(`${ROUTES.API.TIMESHEET}?${queryParams.toString()}`);
      processWeeklyEntries(response.data.results || response.data, skipUpdateIfSame); // Adjust based on actual API response structure
      if (!skipUpdateIfSame) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (!skipUpdateIfSame) {
        toast({ title: 'Error', description: 'Failed to fetch time entries', variant: 'destructive' });
        setIsLoading(false);
      }
    }
  };

  const processWeeklyEntries = (entries: any[], skipUpdateIfSame: boolean = false) => {
    const groupedEntriesMap: Map<string, WeeklyMatrixEntry> = new Map();
    entries.forEach((entry, idx) => {
      const entryHours = typeof entry.hours === 'number' ? entry.hours : parseFloat(entry.hours || '0');
      if (isNaN(entryHours)) return; // Skip entries with invalid hours

      // Group by matter, description, billable status AND user when viewing all users
      const key = `${entry.matter}-${entry.description}-${entry.billable}-${entry.user || 'unknown'}`;
      if (!groupedEntriesMap.has(key)) {
        const matter = matters.find(m => m.id === entry.matter);
        groupedEntriesMap.set(key, {
          tempKey: `row-${entry.matter}-${entry.user}-${idx}`, // Add a temporary unique key
          matter: entry.matter,
          description: entry.description,
          billable: entry.billable,
          days: {},
          matterName: formatMatterName(entry.matter),
          reviewed: entry.reviewed, // Keep track of reviewed status if needed
          userId: entry.user, // Store the user ID
          _sortGroup: matter?.group_name || '', // Store for sorting
          _sortName: matter?.name || '', // Store for sorting
        });
      }

      const dayKey = format(parseISO(entry.date), 'yyyy-MM-dd');
      const existingRow = groupedEntriesMap.get(key);
      if (existingRow) {
        // If an entry for this day already exists in the map (due to multiple entries for the same day/key from API), sum the hours.
        // Use the ID of the first entry encountered for this day/key combination.
        if (existingRow.days[dayKey]) {
          existingRow.days[dayKey].hours = (existingRow.days[dayKey].hours || 0) + entryHours;
          // Keep the first entryId encountered
          if (!existingRow.days[dayKey].entryId) {
             existingRow.days[dayKey].entryId = entry.id;
          }
        } else {
          existingRow.days[dayKey] = {
            hours: entryHours,
            entryId: entry.id
          };
        }
      }
    });

    const groupedEntries = Array.from(groupedEntriesMap.values());
    
    // If skipUpdateIfSame is true, preserve the current order by matching existing entries
    if (skipUpdateIfSame && weeklyEntries.length > 0) {
      // Create a map of existing entries by their unique key for quick lookup
      const existingOrderMap = new Map<string, number>();
      weeklyEntries.forEach((entry, index) => {
        if (entry.matter) { // Skip the empty "new" row
          const key = `${entry.matter}-${entry.description}-${entry.billable}-${entry.userId}`;
          existingOrderMap.set(key, index);
        }
      });
      
      // Sort grouped entries to match the existing order
      groupedEntries.sort((a, b) => {
        const keyA = `${a.matter}-${a.description}-${a.billable}-${a.userId}`;
        const keyB = `${b.matter}-${b.description}-${b.billable}-${b.userId}`;
        const orderA = existingOrderMap.get(keyA) ?? 9999;
        const orderB = existingOrderMap.get(keyB) ?? 9999;
        return orderA - orderB;
      });
    } else {
      // Sort entries alphabetically by group name, then matter name
      // Only sort on initial load (when skipUpdateIfSame is false) to avoid disrupting active data entry
      groupedEntries.sort((a, b) => {
        const groupA = (a._sortGroup || '').toLowerCase();
        const groupB = (b._sortGroup || '').toLowerCase();
        if (groupA !== groupB) {
          return groupA.localeCompare(groupB);
        }
        return (a._sortName || '').toLowerCase().localeCompare((b._sortName || '').toLowerCase());
      });
    }
    
    // Add the initial empty row for new entries
    groupedEntries.push({
      tempKey: `new-row-${Date.now()}`,
      matter: 0,
      description: '',
      billable: true,
      days: {},
      matterName: '',
      userId: selectedUserId !== null ? selectedUserId : user?.id // Use selected user or current user
    });
    
    // If skipUpdateIfSame is true, compare with current state
    if (skipUpdateIfSame) {
      const hasChanges = !areEntriesEqual(weeklyEntries, groupedEntries);
      if (hasChanges) {
        setWeeklyEntries(groupedEntries);
      }
    } else {
      setWeeklyEntries(groupedEntries);
    }
  };
  
  // Helper function to compare two entry arrays
  const areEntriesEqual = (current: WeeklyMatrixEntry[], incoming: WeeklyMatrixEntry[]): boolean => {
    // Exclude the last "new row" from comparison
    const currentData = current.slice(0, -1);
    const incomingData = incoming.slice(0, -1);
    
    if (currentData.length !== incomingData.length) return false;
    
    // Create comparable keys for both arrays
    const currentKeys = new Set(
      currentData.map(e => `${e.matter}-${e.description}-${e.billable}-${e.userId}-${JSON.stringify(e.days)}`)
    );
    const incomingKeys = new Set(
      incomingData.map(e => `${e.matter}-${e.description}-${e.billable}-${e.userId}-${JSON.stringify(e.days)}`)
    );
    
    if (currentKeys.size !== incomingKeys.size) return false;
    
    for (const key of currentKeys) {
      if (!incomingKeys.has(key)) return false;
    }
    
    return true;
  };

  const handleExport = async (export_format: 'pdf' | 'excel') => {
    // ... existing handleExport logic ...
    try {
      // Build query parameters based on current filters
      const queryParams = new URLSearchParams();

      // Add user filter if a specific user is selected
      if (selectedUserId !== null) {
        queryParams.append('user_id', selectedUserId.toString());
      }

      // Add date filters for the week
      const weekStart = format(currentWeekStart, 'yyyy-MM-dd');
      const weekEnd = format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      queryParams.append('start_date', weekStart);
      queryParams.append('end_date', weekEnd);

      // Create the endpoint URL
      const endpoint = export_format === 'pdf'
        ? `${ROUTES.API.TIMESHEET}export_pdf/?${queryParams}`
        : `${ROUTES.API.TIMESHEET}export_excel/?${queryParams}`;

      // Make the API call
      const response = await api.get(endpoint, {
        responseType: 'blob',
      });

      // Create a download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Set appropriate filename
      const dateRange = `${format(currentWeekStart, 'yyyy-MM-dd')}_to_${format(daysOfWeek[daysOfWeek.length - 1], 'yyyy-MM-dd')}`;

      link.setAttribute('download', `timesheet_${dateRange}.${export_format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: 'Success',
        description: `Timesheet exported as ${export_format.toUpperCase()} successfully`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Error',
        description: `Failed to export timesheet as ${export_format.toUpperCase()}`,
        variant: 'destructive',
      });
    }
  };

  // --- New API Helper Functions ---

  const createOrUpdateDayEntry = async (index: number, dayKey: string, hours: number) => {
    const updatedEntries = [...weeklyEntries];
    const entry = updatedEntries[index];
    const dayEntry = entry.days[dayKey];

    // Basic validation before saving
    if (!entry.matter || !entry.description) {
      toast({ title: 'Missing Info', description: 'Please select a matter and enter a description.'});
      // Revert input value if save fails due to missing info
      setInputValues(prev => ({ ...prev, [`${index}-${dayKey}`]: decimalToHHMM(dayEntry?.hours) }));
      return;
    }
     if (hours <= 0) {
        // If hours are zero or less, treat as deletion if entry exists
        if (dayEntry?.entryId) {
            await deleteDayEntry(index, dayKey, dayEntry.entryId);
        } else {
             // If no entryId, just remove from local state
             setInputValues(prev => {
                const newState = {...prev};
                delete newState[`${index}-${dayKey}`];
                return newState;
             });
             setWeeklyEntries(prevEntries => {
                const newEntries = [...prevEntries];
                delete newEntries[index].days[dayKey];
                return newEntries;
             });
        }
        return; // Stop processing if hours are not positive
    }


    const payload = {
      date: dayKey,
      matter: entry.matter,
      description: entry.description,
      billable: entry.billable,
      hours: hours,
      user: entry.userId || selectedUserId || user?.id // Use entry's userId, fallback to selectedUserId, then current user
    };

    try {
      let response;
      if (dayEntry?.entryId) {
        // Update existing entry
        response = await api.patch(`${ROUTES.API.TIMESHEET}${dayEntry.entryId}/`, payload);
        toast({ title: 'Success', description: 'Time entry updated' });
      } else {
        // Create new entry
        response = await api.post(ROUTES.API.TIMESHEET, payload);
        // Update state with the new entryId
        updatedEntries[index].days[dayKey] = { hours: hours, entryId: response.data.id };
        toast({ title: 'Success', description: 'Time entry created' });
      }
       // Update the state with potentially new hours/entryId
       updatedEntries[index].days[dayKey] = { hours: hours, entryId: dayEntry?.entryId ?? response?.data.id };
       setWeeklyEntries(updatedEntries);

       // Remember the matter for recents-first ordering in matter comboboxes
       pushRecentMatter(String(entry.matter));

       // Clear the specific input value from temp state after successful save
       setInputValues(prev => {
           const newState = {...prev};
           delete newState[`${index}-${dayKey}`];
           return newState;
       });

       // Add a new row if the modification was in the last row and it's now valid
       checkAndAddNewRowIfNeeded(index);

       // Refresh timesheets to get any automatic system changes (billable status, hours, etc.)
       setTimeout(() => {
         fetchTimeEntries(true); // Skip update if data is the same
       }, 500); // Small delay to allow server processing

    } catch (error: any) {
      console.error('Save error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save time entry';
      toast({ title: 'Error', description: errorMsg, variant: 'destructive' });
      // Revert input value on error
      setInputValues(prev => ({ ...prev, [`${index}-${dayKey}`]: decimalToHHMM(dayEntry?.hours) }));
    }
  };

  const deleteDayEntry = async (index: number, dayKey: string, entryId: number) => {
    const updatedEntries = [...weeklyEntries];
    try {
      await api.delete(`${ROUTES.API.TIMESHEET}${entryId}/`);
      // Remove the day entry from the state
      delete updatedEntries[index].days[dayKey];

      // If the row becomes empty and wasn't the designated "new" row, remove it
      if (Object.keys(updatedEntries[index].days).length === 0 && updatedEntries[index].matter !== 0) {
         // Only remove if it's not the placeholder new row
         if(index !== updatedEntries.length -1) { // Check if it's not the last row (potential new row)
            updatedEntries.splice(index, 1);
         }
      }

      setWeeklyEntries(updatedEntries);
       // Clear the specific input value from temp state after successful delete
       setInputValues(prev => {
           const newState = {...prev};
           delete newState[`${index}-${dayKey}`];
           return newState;
       });
      toast({ title: 'Success', description: 'Time entry deleted' });
      
      // Refresh timesheets to get any automatic system changes
      setTimeout(() => {
        fetchTimeEntries(true); // Skip update if data is the same
      }, 300); // Small delay to allow server processing
      
    } catch (error) {
      console.error('Delete error:', error);
      toast({ title: 'Error', description: 'Failed to delete time entry', variant: 'destructive' });
       // Revert input value on error - set back to 00:00 or empty? Let's keep it empty.
       setInputValues(prev => ({ ...prev, [`${index}-${dayKey}`]: '' }));
    }
  };

  // Update existing day entries when row metadata (matter, desc, billable) changes
  const updateExistingDayEntriesForRow = async (index: number) => {
      const entry = weeklyEntries[index];
      if (!entry.matter || !entry.description) return; // Need matter and desc

      for (const [dayKey, day] of Object.entries(entry.days)) {
          if (day.entryId && day.hours > 0) { // Only update existing entries with hours
              const payload = {
                  date: dayKey,
                  matter: entry.matter,
                  description: entry.description,
                  billable: entry.billable,
                  hours: day.hours, // Keep existing hours
                  user: entry.userId || selectedUserId || user?.id // Use entry's userId, fallback to selectedUserId, then current user
              };
              try {
                  await api.patch(`${ROUTES.API.TIMESHEET}${day.entryId}/`, payload);
              } catch (error) {
                   console.error(`Failed to update entry ${day.entryId} for row ${index}`, error);
                   // Consider adding a specific toast message here
                   toast({ title: 'Update Warning', description: `Failed to update time for ${dayKey}. Please check manually.`});
              }
          }
      }
       toast({ title: 'Success', description: 'Row details updated for existing time entries.' });
       
       // Only refresh if we actually updated entries
       setTimeout(() => {
         fetchTimeEntries(true); // Skip update if data is the same
       }, 500); // Small delay to allow server processing
  };


  // --- Modified Handlers ---

  const handleInputChange = (index: number, dayKey: string, value: string) => {
    setInputValues(prev => ({ ...prev, [`${index}-${dayKey}`]: value }));
    
    // Trigger debounced save while typing
    const hours = hhmmToDecimal(value);
    if (value.trim() !== '') { // Only save if there's actually content
      debouncedSaveTimeEntry(index, dayKey, hours, 2000); // Longer delay while typing
    }
  };

  const handleBlur = (index: number, dayKey: string) => {
    const inputValue = inputValues[`${index}-${dayKey}`];
    // If inputValue is undefined, it means no change was made, so do nothing.
    if (inputValue === undefined) return;

    const currentHoursInState = weeklyEntries[index]?.days[dayKey]?.hours;
    const formattedCurrentHours = decimalToHHMM(currentHoursInState);

    // If the input value hasn't actually changed from the stored state, do nothing.
    // Handles cases like blurring without making a change, or typing '1:00' when it was already '1:00'.
    // Also check if input is empty/zero and state hours are also zero/undefined.
     if (inputValue === formattedCurrentHours || (!inputValue && !currentHoursInState)) {
        // If value is unchanged, clear from temp state if necessary and return
         setInputValues(prev => {
           const newState = {...prev};
           delete newState[`${index}-${dayKey}`];
           return newState;
         });
        return;
    }

    const hours = hhmmToDecimal(inputValue); // Converts "HH:MM" or decimal string to decimal number

    // Use debounced save on blur instead of immediate save
    debouncedSaveTimeEntry(index, dayKey, hours, 500); // Shorter delay on blur
  };

  const handleWeeklyChange = (index: number, field: 'matter' | 'description' | 'billable', value: any) => {
    const updatedEntries = [...weeklyEntries];
    const entry = updatedEntries[index];
    let needsMetadataUpdate = false;

    if (field === 'matter') {
      entry.matter = Number(value);
      entry.matterName = formatMatterName(Number(value));
       // Check if there are existing entries in this row to update
       if (Object.values(entry.days).some(d => d.entryId)) {
           needsMetadataUpdate = true;
       }
    } else if (field === 'description') {
      entry.description = value;
       if (Object.values(entry.days).some(d => d.entryId)) {
           needsMetadataUpdate = true;
       }
    } else if (field === 'billable') {
      entry.billable = value;
       if (Object.values(entry.days).some(d => d.entryId)) {
           needsMetadataUpdate = true;
       }
    }

    setWeeklyEntries(updatedEntries);

     // If metadata changed and there are existing entries, update them
     if (needsMetadataUpdate) {
         updateExistingDayEntriesForRow(index);
     }
     // Don't refresh when just changing metadata on new rows without time entries

    // Add a new row if the modification was in the last row and it's now potentially valid
    checkAndAddNewRowIfNeeded(index);
  };

   // Function to add a new row if needed
   const checkAndAddNewRowIfNeeded = (modifiedIndex: number) => {
       setWeeklyEntries(currentEntries => {
           const lastIndex = currentEntries.length - 1;
           // Check if the modified row is the last one AND if it has some data entered (matter or desc or any day entry)
           if (modifiedIndex === lastIndex &&
               (currentEntries[lastIndex].matter || currentEntries[lastIndex].description || Object.keys(currentEntries[lastIndex].days).length > 0))
           {
               // Check if the last row isn't already the placeholder row we just added
               const alreadyHasNewRow = currentEntries[lastIndex + 1]?.matter === 0 && !currentEntries[lastIndex + 1]?.description && Object.keys(currentEntries[lastIndex + 1]?.days).length === 0;

               if (!alreadyHasNewRow) {
                   return [
                       ...currentEntries,
                       {
                           tempKey: `new-row-${Date.now()}`, // Ensure unique key
                           matter: 0,
                           description: '',
                           billable: true,
                           days: {},
                           matterName: '',
                           userId: selectedUserId !== null ? selectedUserId : user?.id // Use selected user or current user
                       }
                   ];
               }
           }
           return currentEntries; // Return unchanged state if no new row needed
       });
   };


  // Delete an entire row and all its day entries
  const deleteRow = async (index: number) => {
    const entry = weeklyEntries[index];
    // Don't delete the empty placeholder row
    if (!entry.matter && !entry.description && Object.keys(entry.days).length === 0) return;
    
    // Delete all day entries from the server
    const dayEntries = Object.entries(entry.days).filter(([, d]) => d.entryId);
    try {
      await Promise.all(
        dayEntries.map(([, d]) => api.delete(`${ROUTES.API.TIMESHEET}${d.entryId}/`))
      );
      
      setWeeklyEntries(prev => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
      
      // Clear related input values
      setInputValues(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(key => {
          if (key.startsWith(`${index}-`)) delete newState[key];
        });
        return newState;
      });
      
      toast({ title: 'Success', description: 'Row deleted' });
      setTimeout(() => fetchTimeEntries(true), 300);
    } catch (error) {
      console.error('Delete row error:', error);
      toast({ title: 'Error', description: 'Failed to delete row entries', variant: 'destructive' });
    }
  };

  // Get user display name helper  
  const getUserName = (userId: number | undefined) => {
    if (!userId) return '';
    const u = users.find(usr => usr.id === userId);
    return u ? `${u.first_name} ${u.last_name}` : '';
  };

  // Calculate total hours safely
  const calculateWeeklyTotal = (days: WeeklyMatrixEntry['days']) => {
    const total = Object.values(days).reduce((sum, day) => {
      const hours = typeof day.hours === 'number' ? day.hours : 0;
      return sum + (isNaN(hours) ? 0 : hours);
    }, 0);
    return isNaN(total) ? '0.00' : total.toFixed(2);
  };

  // Fetch entries when parameters change
  useEffect(() => {
    // Check if matters data is available (or store is not loading) before fetching
    if (!storeLoading) { // Assuming matters are fetched via fetchAll in useStore
      fetchTimeEntries();
    }
  }, [currentWeekStart, selectedUserId, storeLoading]); // Removed matters.length dependency if fetchAll handles it

  // Auto-resize all textareas when entries change
  useEffect(() => {
    const timer = setTimeout(() => {
      Object.values(textareaRefs.current).forEach(textarea => {
        if (textarea) {
          autoResizeTextarea(textarea);
        }
      });
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(timer);
  }, [weeklyEntries]);

  // Cleanup effect for save timers
  useEffect(() => {
    return () => {
      // Clear all pending save timers on unmount or week change
      Object.values(saveTimers.current).forEach(timer => clearTimeout(timer));
      saveTimers.current = {};
    };
  }, [currentWeekStart]); // Clear timers when week changes

  // Final cleanup on component unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  // User selector options: self first, then the rest, then "All users" (if permitted)
  const userOptions = [
    { value: String(user?.id), label: 'My time entries' },
    ...users
      .filter(u => u.id !== user?.id)
      .map(u => ({ value: String(u.id), label: `${u.first_name} ${u.last_name}` })),
    ...((isSuperUser || isAdmin || isManager) ? [{ value: 'all', label: 'All users' }] : []),
  ];

  return (
    <Layout>
      <PageShell>
        <PageHeader
          title="Weekly timesheet"
          description={new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        />
        <PageBody>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild>
              <Link to={ROUTES.TIMESHEET_LIST}>
                <List className="w-4 h-4 mr-2" />List View
              </Link>
            </Button>
            
            <Button variant="outline" onClick={() => fetchTimeEntries()} disabled={isLoading}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            
             {/* Export Button */}
             <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport('pdf')}>
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('excel')}>
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
          </div>

          {canViewAllUsers && (
            <ComboboxSelect
              options={userOptions}
              value={selectedUserId === null ? 'all' : String(selectedUserId)} // Handle null case for 'all'
              onValueChange={(value) => {
                const newUserId = value === 'all' ? null : (value ? Number(value) : user?.id || null);
                setSelectedUserId(newUserId);
              }}
              placeholder="Select user"
              searchPlaceholder="Search users..."
              emptyMessage="No users found."
              preserveOrder
              triggerClassName="w-[200px]"
            />
          )}
        </div>

        {/* Removed redundant export button section */}

        <Card>
          <CardHeader>
            <CardTitle>
              Week: {format(currentWeekStart, "dd MMM")} -{" "}
              {format(daysOfWeek[daysOfWeek.length - 1], "dd MMM yyyy")}
            </CardTitle>
            <CardDescription>Enter your time for each matter and day (e.g., 1:30 or 1.5)</CardDescription>
            <div className="flex space-x-2 mt-2">
              <Button variant="outline" size="sm" onClick={previousWeek}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Previous Week
              </Button>
              <Button variant="outline" size="sm" onClick={currentWeek}>
                Current Week
              </Button>
              <Button variant="outline" size="sm" onClick={nextWeek}>
                Next Week
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <DatePicker
                value={toISODate(currentWeekStart)}
                onChange={(iso) => {
                  if (iso) setCurrentWeekStart(startOfWeek(parseISO(iso), { weekStartsOn: 1 }));
                }}
                clearable={false}
                placeholder="Jump to week"
                className="h-8 w-auto"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="py-8 text-center">Loading...</div>
            ) : (
              <div className="w-full">
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b-2">
                        {selectedUserId === null && (
                          <TableHead className="min-w-[120px] w-[12%] font-semibold">User</TableHead>
                        )}
                        <TableHead className="min-w-[250px] max-w-[350px] w-[30%] font-semibold">Matter</TableHead>
                        <TableHead className="min-w-[180px] max-w-[250px] w-[20%] font-semibold">Description</TableHead>
                        <TableHead className="w-[60px] min-w-[60px] text-center font-semibold">
                          Bill
                        </TableHead>
                        {daysOfWeek.map((day) => (
                          <TableHead
                            key={day.toISOString()}
                            className="w-[70px] min-w-[70px] text-center font-semibold"
                          >
                            {format(day, "EEE d")}
                          </TableHead>
                        ))}
                        <TableHead className="w-[70px] min-w-[70px] text-center font-semibold">
                          Total
                        </TableHead>
                        <TableHead className="w-[50px] min-w-[50px] text-center font-semibold">
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody>
                    {weeklyEntries.map((entry, index) => (
                      // Use the temporary key for React list rendering
                      <TableRow key={entry.tempKey || index} className="border-b hover:bg-muted/30 transition-colors">
                        {selectedUserId === null && (
                          <TableCell className="align-middle p-2">
                            <div className="text-sm text-muted-foreground truncate">
                              {getUserName(entry.userId)}
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="align-middle p-2">
                          {/* Matter ComboboxSelect */}
                          <ComboboxSelect
                            options={matterOptions}
                            value={entry.matter ? String(entry.matter) : ""}
                            onValueChange={(value) =>
                              handleWeeklyChange(index, "matter", value)
                            }
                            placeholder="Matter"
                            searchPlaceholder="Search matters..."
                            emptyMessage="No matters found."
                            preserveOrder
                            triggerClassName="h-auto min-h-[40px] text-sm text-left"
                            renderValue={(option) => (
                              option ? (
                                <div 
                                  className="whitespace-normal break-words leading-tight text-left"
                                  style={{
                                    minHeight: '24px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    paddingTop: '2px',
                                    paddingBottom: '2px'
                                  }}
                                >
                                  {option.label}
                                </div>
                              ) : null
                            )}
                            renderOption={(option) => (
                              <div className="whitespace-normal break-words">
                                {option.label}
                              </div>
                            )}
                            // disabled={entry.reviewed} // Optionally disable if reviewed
                          />
                        </TableCell>
                        <TableCell className="align-middle p-2">
                          {/* Description Input */}
                          <Textarea
                            ref={(el) => {
                              const key = `desc-${index}`;
                              textareaRefs.current[key] = el;
                              // Auto-resize on mount/update
                              if (el && entry.description) {
                                setTimeout(() => autoResizeTextarea(el), 0);
                              }
                            }}
                            value={entry.description}
                            onChange={(e) =>
                              handleWeeklyChange(index, "description", e.target.value)
                            }
                            placeholder="Description"
                            className="min-h-[40px] text-sm resize-none overflow-hidden w-full"
                            rows={1}
                            onInput={(e) => {
                              // Auto-resize functionality
                              const target = e.target as HTMLTextAreaElement;
                              autoResizeTextarea(target);
                            }}
                            // disabled={entry.reviewed} // Optionally disable if reviewed
                          />
                        </TableCell>
                        <TableCell className="text-center align-middle p-2">
                          {/* Billable Switch */}
                          <Switch
                            checked={entry.billable}
                            onCheckedChange={(checked) =>
                              handleWeeklyChange(index, "billable", checked)
                            }
                            // disabled={entry.reviewed} // Optionally disable if reviewed
                          />
                        </TableCell>
                        {/* Time Input Cells */}
                        {daysOfWeek.map((day) => {
                          const dayKey = format(day, "yyyy-MM-dd");
                          const dayEntry = entry.days[dayKey];
                          const inputKey = `${index}-${dayKey}`;
                          const displayValue = inputValues[inputKey] !== undefined
                            ? inputValues[inputKey] // Show temp input value if editing
                            : decimalToHHMM(dayEntry?.hours); // Otherwise show formatted state value

                          const hasZeroHours = !dayEntry?.hours || dayEntry.hours === 0;
                          
                          return (
                            <TableCell key={dayKey} className="text-center align-middle p-1">
                              <Input
                                value={displayValue}
                                onChange={(e) => handleInputChange(index, dayKey, e.target.value)}
                                onBlur={() => handleBlur(index, dayKey)}
                                placeholder="0:00"
                                className={`h-10 w-full text-center text-sm font-medium px-1 ${hasZeroHours ? 'bg-muted/80 text-muted-foreground/60' : 'bg-background'}`}
                                // disabled={entry.reviewed} // Optionally disable if reviewed
                              />
                            </TableCell>
                          );
                        })}
                        {/* Row Total */}
                        <TableCell className="text-center align-middle font-medium p-2">
                          {decimalToHHMM(parseFloat(calculateWeeklyTotal(entry.days)))}
                        </TableCell>
                        {/* Delete Row Button */}
                        <TableCell className="text-center align-middle p-1">
                          {(entry.matter !== 0 || entry.description || Object.keys(entry.days).length > 0) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => deleteRow(index)}
                              title="Delete row"
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Daily Totals Row */}
                    <TableRow className="bg-muted/10 font-medium">
                      <TableCell colSpan={selectedUserId === null ? 4 : 3} className="text-right font-bold">
                        Daily Totals
                      </TableCell>
                      {daysOfWeek.map((day) => {
                        const dayKey = format(day, "yyyy-MM-dd");
                        const dayTotal = weeklyEntries.reduce((total, entry) => {
                          // Sum hours directly from the main state, not temp input values
                          const hours = entry.days[dayKey]?.hours || 0;
                          return total + (typeof hours === "number" && !isNaN(hours) ? hours : 0);
                        }, 0);

                        return (
                          <TableCell key={dayKey} className="text-center font-bold">
                            {decimalToHHMM(dayTotal)}
                          </TableCell>
                        );
                      })}
                      {/* Grand Total */}
                      <TableCell className="text-center font-bold">
                        {decimalToHHMM(
                          weeklyEntries.reduce((grandTotal, entry) => {
                            // Sum totals directly from the main state
                            return grandTotal + parseFloat(calculateWeeklyTotal(entry.days) || "0");
                          }, 0)
                        )}
                      </TableCell>
                      {/* Empty cell for delete column */}
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </PageBody>
      </PageShell>
    </Layout>
  );
}